import React, { useState } from 'react';
import { NewProjectUserFormState } from 'src/app/admin/views/user-management/state/userManagementSlice';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import useAuthService from 'src/shared/hooks/useAuthService';
import { UserGroup } from 'src/shared/types';
import styles from './roleCheckbox.module.scss';

type OrganisationalViewerCheckboxProps = {
    groups: UserGroup[];
    defaultGroups?: UserGroup[] | null;
    /** Whether the user being created is internal — the caller's job, since "new user" and "user
     * being edited" are tracked in separate Redux slices and this component must not guess. */
    isInternalUser: boolean;
    onChange: (fragment: Partial<NewProjectUserFormState['formState']>) => void;
};

/**
 * ORGANISATIONAL_VIEWER — a real Cognito group, not a Role. Only offered to a super admin creating a
 * brand-new internal user: CreateUserInput.groups is the only mutation that persists group membership
 * (see #797's backend allow-list). There is no equivalent for an existing user (UpdateUserInput has no
 * groups field) — callers editing an existing user must not render this component at all.
 */
const OrganisationalViewerCheckbox = (props: OrganisationalViewerCheckboxProps) => {
    const { groups, onChange, defaultGroups = [], isInternalUser } = props;
    const [checkedGroups, setCheckedGroups] = useState<UserGroup[] | null>(defaultGroups);
    const { isCurrentUserSuperAdmin } = useAuthService();

    const handleChange = (isChecked: boolean) => {
        const currentGroups = Array.isArray(groups) ? groups : [];
        const updatedGroups = isChecked
            ? [...currentGroups, UserGroup.ORGANISATIONAL_VIEWER]
            : currentGroups.filter(group => group !== UserGroup.ORGANISATIONAL_VIEWER);
        onChange({ groups: updatedGroups });
        setCheckedGroups(updatedGroups);
    };

    if (!isCurrentUserSuperAdmin || !isInternalUser) {
        return null;
    }

    return (
        <div data-testid='user-management-organisational-viewer-checkbox' className={styles.roleOption}>
            <Checkbox
                label='Organisational Viewer'
                onChange={handleChange}
                checked={Boolean(checkedGroups?.includes(UserGroup.ORGANISATIONAL_VIEWER))}
            />
            <p className={styles.roleDescription}>
                Read-only visibility across every customer&apos;s projects, survey designs and dashboards.
            </p>
        </div>
    );
};

export default OrganisationalViewerCheckbox;
